import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  CircularProgress,
  Grid
} from '@mui/material';
import { WaitingListEntry } from '../../firebase/services/waitingListService';
import { getAvailableSlips, Slip } from '../../firebase/services/slipService';

interface TransferToSlipDialogProps {
  open: boolean;
  onClose: () => void;
  entry: WaitingListEntry;
  onTransfer: (entry: WaitingListEntry, slipId: string, startDate: Date) => void;
}

const TransferToSlipDialog: React.FC<TransferToSlipDialogProps> = ({
  open,
  onClose,
  entry,
  onTransfer
}) => {
  const [availableSlips, setAvailableSlips] = useState<Slip[]>([]);
  const [selectedSlip, setSelectedSlip] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadAvailableSlips = async () => {
      try {
        setLoading(true);
        setError(null);
        const slips = await getAvailableSlips({
          minLength: entry.vesselLength,
          type: 'Standard' // Default to standard slip type
        });
        setAvailableSlips(slips);
      } catch (err) {
        setError('Failed to load available slips');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      loadAvailableSlips();
    }
  }, [open, entry]);

  const handleTransfer = () => {
    if (!selectedSlip || !startDate) {
      setError('Please select a slip and start date');
      return;
    }

    onTransfer(entry, selectedSlip, new Date(startDate));
  };

  const getFilteredSlips = () => {
    return availableSlips.filter(slip => 
      slip.length >= entry.vesselLength
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Transfer to Slip - {entry.firstName} {entry.lastName}
      </DialogTitle>
      <DialogContent>
        <Box mb={3}>
          <Typography variant="subtitle1" gutterBottom>
            Vessel Details
          </Typography>
          <Typography variant="body2">
            {entry.vesselName} ({entry.vesselLength}' - {entry.vesselYear} {entry.vesselMake} {entry.vesselModel})
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Select Available Slip</InputLabel>
                <Select
                  value={selectedSlip}
                  label="Select Available Slip"
                  onChange={(e) => setSelectedSlip(e.target.value)}
                >
                  {getFilteredSlips().map((slip) => (
                    <MenuItem key={slip.id} value={slip.id}>
                      Slip {slip.slipNumber} - {slip.type} ({slip.length}' x {slip.width}')
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        )}

        {!loading && getFilteredSlips().length === 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            No compatible slips are currently available. Please check back later or contact the marina office.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleTransfer}
          disabled={!selectedSlip || !startDate || loading}
        >
          Complete Transfer
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TransferToSlipDialog; 